/**
 * ElevenLabs Voice Provider
 *
 * Implementation of IVoiceProvider for ElevenLabs Conversational AI 2.0.
 * Uses WebSocket for real-time conversation with STT, LLM, and TTS.
 */

import type {
  IVoiceProvider,
  VoiceInfo,
  SynthesisOptions,
  ConversationOptions,
  AudioStream,
  HealthStatus,
  VoiceSession,
  VoiceSessionState,
  VoiceSessionEvents,
  TranscriptionResult,
} from './IVoiceProvider'

// ElevenLabs specific types
interface ElevenLabsVoice {
  voice_id: string
  name: string
  description?: string
  preview_url?: string
  labels?: {
    language?: string
    gender?: string
  }
}

interface ElevenLabsMessage {
  type: string
  // Various payloads depending on type
  text?: string
  audio?: string // Base64 encoded audio
  user_transcript?: string
  user_transcript_final?: boolean
  is_final?: boolean
  error?: string
}

const ELEVENLABS_API_BASE = 'https://api.elevenlabs.io/v1'
const ELEVENLABS_WS_BASE = 'wss://api.elevenlabs.io/v1/convai/conversation'

export class ElevenLabsProvider implements IVoiceProvider {
  readonly id = 'elevenlabs'
  readonly name = 'ElevenLabs'
  readonly type = 'elevenlabs' as const

  private apiKey: string | null = null
  private voices: VoiceInfo[] = []

  async initialize(apiKey: string): Promise<void> {
    this.apiKey = apiKey
    // Verify the API key by fetching voices
    await this.getVoices()
  }

  isInitialized(): boolean {
    return this.apiKey !== null
  }

  async getVoices(): Promise<VoiceInfo[]> {
    if (!this.apiKey) {
      throw new Error('ElevenLabs provider not initialized')
    }

    try {
      const response = await fetch(`${ELEVENLABS_API_BASE}/voices`, {
        headers: {
          'xi-api-key': this.apiKey,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.statusText}`)
      }

      const data = await response.json()
      this.voices = (data.voices || []).map((voice: ElevenLabsVoice) => ({
        id: voice.voice_id,
        name: voice.name,
        description: voice.description,
        previewUrl: voice.preview_url,
        language: voice.labels?.language,
        gender: voice.labels?.gender as 'male' | 'female' | 'neutral' | undefined,
      }))

      return this.voices
    } catch (error) {
      console.error('Failed to fetch ElevenLabs voices:', error)
      throw error
    }
  }

  async createSession(
    options: ConversationOptions,
    events: VoiceSessionEvents
  ): Promise<VoiceSession> {
    if (!this.apiKey) {
      throw new Error('ElevenLabs provider not initialized')
    }

    return new ElevenLabsSession(this.apiKey, options, events)
  }

  async synthesize(options: SynthesisOptions): Promise<AudioStream> {
    if (!this.apiKey) {
      throw new Error('ElevenLabs provider not initialized')
    }

    const response = await fetch(
      `${ELEVENLABS_API_BASE}/text-to-speech/${options.voiceId}/stream`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: options.text,
          model_id: options.model || 'eleven_flash_v2_5',
          voice_settings: {
            stability: options.stability ?? 0.5,
            similarity_boost: options.similarityBoost ?? 0.75,
            style: options.style ?? 0,
            use_speaker_boost: options.useSpeakerBoost ?? true,
          },
          ...(options.pronunciationDictionaryLocators?.length ? {
            pronunciation_dictionary_locators: options.pronunciationDictionaryLocators,
          } : {}),
        }),
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to synthesize speech: ${response.statusText}`)
    }

    return {
      stream: response.body!,
      mimeType: 'audio/mpeg',
      sampleRate: 44100,
    }
  }

  async healthCheck(): Promise<HealthStatus> {
    if (!this.apiKey) {
      return {
        isHealthy: false,
        message: 'Provider not initialized',
      }
    }

    try {
      const start = Date.now()
      const response = await fetch(`${ELEVENLABS_API_BASE}/user`, {
        headers: {
          'xi-api-key': this.apiKey,
        },
      })
      const latency = Date.now() - start

      return {
        isHealthy: response.ok,
        latency,
        message: response.ok ? 'Connected' : response.statusText,
      }
    } catch (error) {
      return {
        isHealthy: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async destroy(): Promise<void> {
    this.apiKey = null
    this.voices = []
  }
}

/**
 * ElevenLabs Conversation Session
 *
 * Manages a WebSocket connection for real-time conversation.
 */
class ElevenLabsSession implements VoiceSession {
  private ws: WebSocket | null = null
  private _state: VoiceSessionState = 'idle'
  private audioContext: AudioContext | null = null
  private audioQueue: AudioBuffer[] = []
  private isPlaying = false

  constructor(
    private apiKey: string,
    private options: ConversationOptions,
    private events: VoiceSessionEvents
  ) {}

  get state(): VoiceSessionState {
    return this._state
  }

  private setState(newState: VoiceSessionState) {
    this._state = newState
    this.events.onStateChange?.(newState)
  }

  async start(): Promise<void> {
    if (this.ws) {
      throw new Error('Session already started')
    }

    this.setState('connecting')

    // Build the WebSocket URL with query parameters including API key for auth
    const params = new URLSearchParams({
      agent_id: this.options.voiceId, // In conversational AI, voiceId is the agent ID
      xi_api_key: this.apiKey,
    })

    try {
      // Create audio context for playback
      this.audioContext = new AudioContext()

      // Connect to ElevenLabs WebSocket
      this.ws = new WebSocket(`${ELEVENLABS_WS_BASE}?${params}`)
      this.ws.binaryType = 'arraybuffer'

      this.ws.onopen = () => {
        this.setState('connected')

        // Send initialization message
        const initMessage = {
          type: 'conversation_initiation_client_data',
          conversation_config_override: {
            agent: {
              prompt: {
                prompt: this.options.systemPrompt || '',
              },
              first_message: this.options.initialMessage,
              language: this.options.language || 'en',
            },
            tts: {
              voice_id: this.options.voiceId,
            },
          },
        }

        this.ws?.send(JSON.stringify(initMessage))
      }

      this.ws.onmessage = (event) => {
        this.handleMessage(event)
      }

      this.ws.onerror = (error) => {
        console.error('ElevenLabs WebSocket error:', error)
        this.setState('error')
        this.events.onError?.(new Error('WebSocket error'))
      }

      this.ws.onclose = () => {
        this.setState('disconnected')
      }
    } catch (error) {
      this.setState('error')
      throw error
    }
  }

  async stop(): Promise<void> {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    this.audioQueue = []
    this.isPlaying = false
    this.setState('idle')
  }

  sendAudio(audioData: Float32Array | Int16Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send audio: WebSocket not connected')
      return
    }

    // Convert to base64 if necessary and send
    // ElevenLabs expects 16-bit PCM audio at 16kHz
    let buffer: ArrayBuffer

    if (audioData instanceof Float32Array) {
      // Convert Float32 to Int16
      const int16Data = new Int16Array(audioData.length)
      for (let i = 0; i < audioData.length; i++) {
        const s = Math.max(-1, Math.min(1, audioData[i]))
        int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
      buffer = int16Data.buffer
    } else {
      buffer = audioData.buffer
    }

    // Send audio as binary
    this.ws.send(buffer)
  }

  sendText(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send text: WebSocket not connected')
      return
    }

    const message = {
      type: 'user_message',
      text,
    }

    this.ws.send(JSON.stringify(message))
    this.setState('processing')
  }

  interrupt(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    const message = {
      type: 'interruption',
    }

    this.ws.send(JSON.stringify(message))

    // Clear audio queue and stop playback
    this.audioQueue = []
    this.isPlaying = false
    this.setState('listening')
  }

  private handleMessage(event: MessageEvent): void {
    if (typeof event.data === 'string') {
      // JSON message
      try {
        const message = JSON.parse(event.data) as ElevenLabsMessage

        switch (message.type) {
          case 'user_transcript': {
            const transcript: TranscriptionResult = {
              text: message.user_transcript || '',
              isFinal: message.user_transcript_final || false,
            }
            this.events.onTranscript?.(transcript)
            if (transcript.isFinal) {
              this.setState('processing')
            } else {
              this.setState('listening')
            }
            break
          }

          case 'agent_response':
            if (message.text) {
              this.events.onMessage?.(message.text)
            }
            break

          case 'audio':
            if (message.audio) {
              this.handleAudioData(message.audio)
            }
            break

          case 'error':
            this.events.onError?.(new Error(message.error || 'Unknown error'))
            this.setState('error')
            break

          case 'ping':
            // Respond to keep-alive
            if (this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify({ type: 'pong' }))
            }
            break
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    } else if (event.data instanceof ArrayBuffer) {
      // Binary audio data
      this.handleBinaryAudio(event.data)
    }
  }

  private async handleAudioData(base64Audio: string): Promise<void> {
    if (!this.audioContext) return

    try {
      // Decode base64 to ArrayBuffer
      const binaryString = atob(base64Audio)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      // Decode audio
      const audioBuffer = await this.audioContext.decodeAudioData(bytes.buffer.slice(0))
      this.audioQueue.push(audioBuffer)

      if (!this.isPlaying) {
        this.playNextAudio()
      }
    } catch (error) {
      console.error('Failed to decode audio:', error)
    }
  }

  private async handleBinaryAudio(data: ArrayBuffer): Promise<void> {
    if (!this.audioContext) return

    try {
      const audioBuffer = await this.audioContext.decodeAudioData(data.slice(0))
      this.audioQueue.push(audioBuffer)

      if (!this.isPlaying) {
        this.playNextAudio()
      }
    } catch (error) {
      console.error('Failed to decode binary audio:', error)
    }
  }

  private playNextAudio(): void {
    if (!this.audioContext || this.audioQueue.length === 0) {
      this.isPlaying = false
      this.events.onAudioEnd?.()
      this.setState('listening')
      return
    }

    this.isPlaying = true
    this.setState('speaking')
    this.events.onAudioStart?.()

    const buffer = this.audioQueue.shift()!
    const source = this.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(this.audioContext.destination)

    source.onended = () => {
      this.playNextAudio()
    }

    source.start()
  }
}

// Export singleton instance
export const elevenLabsProvider = new ElevenLabsProvider()
